import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Eye } from 'lucide-react'
import { Button } from '@/ui/components/common/Button'
import { Card, CardContent } from '@/ui/components/common/Card'
import { Skeleton } from '@/ui/components/common/Skeleton'
import {
  useGetActiveInvestigation,
  useGetAllInvestigations,
  useSetActiveInvestigation,
  useSetActiveLead,
} from '@/application/services/investigation.service'
import { useGetLeadGroups } from '@/application/services/lead.service'
import { InvestigationAccordion, type GroupShape } from './InvestigationAccordion'
import type { LiveSortOrder } from './LiveHeader'

const LIVE_PAGE_SIZE = 10

// ──────────────────────────────────────────────
// Public section — paginated accordion of investigations & leads
// ──────────────────────────────────────────────

interface InvestigationsAccordionSectionProps {
  sortOrder: LiveSortOrder
}

/**
 * Main listing section: paginated, collapsible accordion of all ongoing
 * investigations and their leads. Each lead row can be expanded to lazily
 * load its facts. Owns: pagination state, accordion open/closed state and
 * the active-investigation/active-lead mutations.
 */
export function InvestigationsAccordionSection({ sortOrder }: InvestigationsAccordionSectionProps) {
  const { t } = useTranslation()
  const { data: investigations, isLoading: investigationsLoading } = useGetAllInvestigations()
  const { data: leadGroups, isLoading: leadsLoading } = useGetLeadGroups()
  const { data: activeInvestigation } = useGetActiveInvestigation()
  const { mutate: setActiveInvestigation } = useSetActiveInvestigation()
  const { mutate: setActiveLead } = useSetActiveLead()

  const [livePage, setLivePage] = useState(1)
  const [openInvestigations, setOpenInvestigations] = useState<Set<string>>(new Set())
  const [openLeads, setOpenLeads] = useState<Set<string>>(new Set())

  const toggleInvestigation = (investigationId: string) =>
    setOpenInvestigations((prev) => {
      const next = new Set(prev)
      if (next.has(investigationId)) next.delete(investigationId)
      else next.add(investigationId)
      return next
    })

  const toggleLead = (investigationId: string) =>
    setOpenLeads((prev) => {
      const next = new Set(prev)
      if (next.has(investigationId)) next.delete(investigationId)
      else next.add(investigationId)
      return next
    })

  // Only show investigations that are currently ongoing
  const ongoingInvestigations = (investigations ?? []).filter(
    (investigation) => investigation.status === 'active',
  )
  const leadGroupMap = new Map(
    (leadGroups ?? []).map((group) => [group.investigation.id, group.leads]),
  )

  // Build groups, sort leads within each, then put the live-active one first
  const allGroups: GroupShape[] = ongoingInvestigations
    .map((investigation) => ({
      investigation: investigation,
      leads: (leadGroupMap.get(investigation.id) ?? []).sort((itemA, itemB) => {
        const diff = new Date(itemA.createdAt).getTime() - new Date(itemB.createdAt).getTime()
        return sortOrder === 'oldest' ? diff : -diff
      }),
    }))
    .sort((itemA, itemB) => {
      const aActive = itemA.investigation.id === activeInvestigation?.id ? 0 : 1
      const bActive = itemB.investigation.id === activeInvestigation?.id ? 0 : 1
      return aActive - bActive
    })

  const totalPages = Math.max(1, Math.ceil(allGroups.length / LIVE_PAGE_SIZE))
  const paginatedGroups = allGroups.slice(
    (livePage - 1) * LIVE_PAGE_SIZE,
    livePage * LIVE_PAGE_SIZE,
  )

  if (leadsLoading || investigationsLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_item, index) => (
          <Skeleton key={index} className="h-14 w-full rounded-xl" />
        ))}
      </div>
    )
  }

  if (ongoingInvestigations.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <Eye className="w-12 h-12 mx-auto text-muted-foreground mb-4 opacity-40" />
          <h3 className="font-semibold mb-2">{t('admin.live_no_ongoing_title')}</h3>
          <p className="text-muted-foreground text-sm">{t('admin.live_no_ongoing_body')}</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {paginatedGroups.map((group) => (
          <InvestigationAccordion
            key={group.investigation.id}
            group={group}
            open={openInvestigations.has(group.investigation.id)}
            onToggle={() => toggleInvestigation(group.investigation.id)}
            openLeads={openLeads}
            onToggleLead={toggleLead}
            isActive={activeInvestigation?.id === group.investigation.id}
            onSetActive={() => setActiveInvestigation(group.investigation.id)}
            onSetActiveLead={(leadId) =>
              setActiveLead({ investigationId: group.investigation.id, leadId })
            }
            activeLeadId={
              activeInvestigation?.id === group.investigation.id
                ? (activeInvestigation.activeLeadId ?? null)
                : null
            }
            activeFactId={
              activeInvestigation?.id === group.investigation.id
                ? (activeInvestigation.activeFactId ?? null)
                : null
            }
          />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setLivePage((page) => Math.max(1, page - 1))}
            disabled={livePage === 1}
          >
            ←
          </Button>
          <span className="text-sm text-muted-foreground">
            {t('admin.pagination_info', { page: livePage, total: totalPages })}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setLivePage((page) => Math.min(totalPages, page + 1))}
            disabled={livePage === totalPages}
          >
            →
          </Button>
        </div>
      )}
    </div>
  )
}
